import { create } from "zustand";
import type { ExtendedWalletClient, SdkClient } from "@sdk/index";
import { useQuickTradeFlowStore } from "./useQuickTradeFlowStore";
import { IxChain } from "@shared/src/config/chains";


export type HostContext = "widget" | "webApp";

export interface MinimalRouter {
  /** Navigate to a new URL */
  push: (url: string) => void;
  /** Replace the current URL without pushing a new history entry */
  replace: (url: string) => void;
  /** Go back in history (optional) */
  back: () => void;
  /** Go to home */
  goToHome: () => void;
  refresh: () => void;
  prefetch: (url: string) => void;
}

interface HostStore {
  context: HostContext;
  router: MinimalRouter;
  isChangingChain: boolean;
  sdk: SdkClient | null;
  analytics: any | null;
  activeChainId: number | null;
  connectorId?: string;
  setConnectorId: (id: string) => void;
  activeChain: IxChain | null;
  /** Update the context ("widget" | "webApp") */
  setContext: (ctx: HostContext) => void;
  /** Provide/override the router implementation */
  setRouter: (router: MinimalRouter) => void;
  /** Set the SDK instance for current host */
  setAnalytics: (analytics: any) => void;
  setAndSwitchChain: (chainId: number | null) => Promise<IxChain | null>;
}

// A no-op router that logs navigation attempts – useful for Storybook/tests.
const noopRouter: MinimalRouter = {
  push: url => {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[useHostStore] push() called before router initialised → ${url}`);
    }
  },
  replace: url => {
    if (process.env.NODE_ENV !== "production") {
      console.warn(`[useHostStore] replace() called before router initialised → ${url}`);
    }
  },
  back: () => {},
  goToHome: () => {},
  refresh: () => {},
  prefetch: () => {},
};

export const useHostStore = create<HostStore>((set, _get) => ({
  context: "webApp",
  router: noopRouter,
  isChangingChain: false,
  setConnectorId: (id: string) => set({ connectorId: id }),
  setAndSwitchChain: async (chainId: number | null) => {
    set({ isChangingChain: true });
    const store = _get();
    const sdk = store.sdk;

    if (!sdk) throw new Error("SDK not initialised");
    const chain = await sdk.auth.switchChain(chainId);
    console.log("usehostore switchChain chain", chain);

    if (sdk.auth.getWalletClient()?.account?.type !== "json-rpc") {
    }

    set({ activeChainId: chainId, activeChain: chain, isChangingChain: false });
    return chain;
  },
  sdk: null,
  analytics: null,
  activeChainId: null,
  activeChain: null,
  setContext: ctx => set({ context: ctx }),
  setRouter: router => set({ router }),
  setAnalytics: analytics => set({ analytics }),
}));

// Convenience helpers ------------------------------------------------------

/**
 * Create a minimal router implementation suitable for the widget environment.
 * ‑ Absolute URLs navigate directly.
 * ‑ Relative URLs are resolved against the web-app base and opened in a new tab.
 */
export const createWidgetRouter = (webAppBaseUrl: string): MinimalRouter => {
  const toAbsolute = (url: string) =>
    url.startsWith("http://") || url.startsWith("https://") ? url : webAppBaseUrl + url;

  return {
    push: url => {
      const absolute = toAbsolute(url);
      window.open(absolute, "_blank", "noopener");
    },
    replace: url => {
      const absolute = toAbsolute(url);
      window.location.replace(absolute);
    },
    back: () => {
      const { currentState, previousState, transitionToState, goToForm } = useQuickTradeFlowStore.getState();
      // If in p2p_flow, navigate back to form
      if (currentState === "p2p_flow") {
        goToForm();
        return;
      }
      // If we have a previous state recorded, transition back to it
      if (previousState) {
        transitionToState(previousState);
        return;
      }
      // Fallback to browser history if nothing else
      window.history.back();
    },
    goToHome: () => {
      useQuickTradeFlowStore.getState().goToForm();
    },
    refresh: () => {
      window.location.reload();
    },
    prefetch: (url: string) => {},
  };
};

/**
 * Initialise the host store for either the widget or web application.
 * @param context        Host context – "widget" or "webApp".
 * @param router         Minimal router implementation for navigation actions.
 * @param sdk            Ix SDK instance for API calls.
 * @param analytics      Optional analytics instance.
 */
export const initHost = ({
  context,
  router,
  sdk,
  analytics,
  activeChainId,
}: {
  context: HostContext;
  router: MinimalRouter;
  sdk: SdkClient;
  analytics: any | null;
  activeChainId?: number | null;
}) => {
  const activeChain = sdk.config.chains.find(c => c.id === activeChainId);
  console.log("initHost", { context, router, sdk, analytics, activeChainId, activeChain });
  // Build the update object, only including `setAndSwitchChain` if it was explicitly provided.

  useHostStore.setState({ context, router, sdk, analytics, activeChainId, activeChain });
  sdk.config.waitForInit().then(() => {
    useHostStore.setState({
      sdk: sdk,
    });
  });
};
